import { PartnerColumn, PartnerData, PartnerStatus } from "./types";
import { mapEnum } from "@/utils/strings";
import { parseMultiSelectOptions } from "@/utils/entityFieldsUtils/shared";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import { APIFieldItem } from "@/pages/settings/types";
import { EmptyFilters, Filters } from "@/services/api/types";
import { camelizeFieldKey } from "@/utils/fieldKeys";

export const partnerStatuses = mapEnum(PartnerStatus, (value) => value);
export const partnerColumnKeys = Object.values(PartnerColumn);

type PartnerStatusColorMap = {
  [K in PartnerStatus]: string;
} & { null: string };

export const partnerStatusColors: PartnerStatusColorMap = {
  [PartnerStatus.ACTIVE]: "var(--charts-positive)",
  [PartnerStatus.INACTIVE]: "var(--warning)",
  [PartnerStatus.ONBOARDING]: "var(--natural)",
  null: "var(--border-field-default)"
};

export const partnersSortByOptions = [
  PartnerColumn.NAME,
  PartnerColumn.STATUS,
  PartnerColumn.CREATED_AT
];

// Type options come from the partners_types lookup, which settingsapi resolves
// into the `type` field's config.options — never from a client-side list.
export const getPartnerTypeOptions = (
  fields: APIFieldItem[]
): { value: string; label: string }[] =>
  parseMultiSelectOptions(
    fields.find((field) => field.fieldKey === PartnerColumn.TYPE)?.config
  ).filter(({ value }) => {
    // Values become stored ids, so anything that isn't a lookup id is dropped.
    const id = Number(value);
    return Number.isInteger(id) && id > 0;
  });

export const getPartnerTypeLabels = (
  fields: APIFieldItem[]
): Record<string, string> =>
  Object.fromEntries(
    getPartnerTypeOptions(fields).map(({ value, label }) => [value, label])
  );

const partnerStatusValues = new Set(
  Object.values(PartnerStatus).filter(
    (value): value is number => typeof value === "number"
  )
);

interface NormalizePartnerTypeAndStatusInput {
  status?: unknown;
  type?: unknown;
}

const toFiniteNumber = (value: unknown): number | null =>
  typeof value === "number" || typeof value === "string"
    ? (Number.isFinite(Number(value)) ? Number(value) : null)
    : null;

export const normalizePartnerTypeAndStatus = (
  data: NormalizePartnerTypeAndStatusInput
): Pick<PartnerData, "status" | "type"> => {
  const rawStatus = toFiniteNumber(data.status);
  const status =
    rawStatus !== null && partnerStatusValues.has(rawStatus)
      ? (rawStatus as PartnerStatus)
      : PartnerStatus.ACTIVE;

  const type = Array.isArray(data.type)
    ? data.type
        .map(toFiniteNumber)
        .filter(
          (value): value is number =>
            value !== null && Number.isInteger(value) && value > 0
        )
    : null;

  return { status, type };
};

// Objects (deals, leads) link to a partner through a custom field of displayType
// PARTNERS whose backend key is account-configurable, so resolve it from the
// entity's fields rather than hardcoding a key. Returns the base filter that
// scopes an object table to the partner, or EmptyFilters when unresolved.
export const getPartnerBaseFilters = (
  fields: APIFieldItem[] | undefined,
  partnerId: string | undefined
): Filters => {
  if (!partnerId || !fields) return EmptyFilters;
  const partnerField = fields.find(
    (field) => field.displayType === FieldDisplayType.PARTNERS
  );
  return partnerField
    ? { [camelizeFieldKey(partnerField.fieldKey)]: [partnerId] }
    : EmptyFilters;
};
